import {ChangeEvent} from 'react';

interface ImagePreviewBoxProps {
  imageUrl?: string | null;
  file?: File | null | any; // Accept any type since Formik might serialize it
  onRemove?: () => void;
  onUpload?: (e: ChangeEvent<HTMLInputElement>) => void;
  variant?: 'sm' | 'md';
  inputId?: string;
  showRemoveButton?: boolean;
  showCheckTitle?: boolean;
}

const ImagePreviewBox = ({
  imageUrl,
  file,
  onUpload,
  variant = 'sm',
  inputId = 'imageUpload',
  showCheckTitle = true,
}: ImagePreviewBoxProps) => {
  // Generate preview URL from file if provided
  const getPreviewUrl = () => {
    if (imageUrl) return imageUrl;

    // Check if file is a valid File object
    if (file instanceof File) {
      try {
        const url = URL.createObjectURL(file);
        return url;
      } catch (error) {
        console.error('Error creating object URL:', error);
        return null;
      }
    }

    // If file is not a File object but has a url or path property (serialized by Formik)
    if (file && typeof file === 'object') {
      // Some form libraries store the file URL
      if (file.url) return file.url;
      if (file.path) return file.path;
      if (file.preview) return file.preview;
    }

    return null;
  };

  const previewUrl = getPreviewUrl();
  const hasImage = !!previewUrl;

  const handleImageClick = () => {
    if (previewUrl) {
      // Open image in new tab
      window.open(previewUrl, '_blank');
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (previewUrl) {
      const link = document.createElement('a');
      link.href = previewUrl;
      link.download = 'document-image.jpg';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <section className="flex flex-col items-start justify-center">
      {showCheckTitle && (
        <span className="pb-2 text-sm text-gray-700">تصویر روی چک</span>
      )}
      {hasImage ? (
        <div className="flex flex-col items-start w-full">
          <div
            className={`${
              variant === 'sm' ? 'size-16' : 'size-24'
            } rounded-lg border-dashed border-[2px] border-gray-700 relative overflow-hidden cursor-pointer group hover:opacity-90 transition-opacity`}
            onClick={handleImageClick}
          >
            {/* Download button on hover */}
            <div
              className={`absolute flex items-center justify-center bg-white/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ${
                variant === 'sm' ? 'left-0 top-0' : 'left-2 top-2 p-1'
              }`}
              onClick={handleDownload}
            >
              <i
                className={`text-blue-600 cursor-pointer icon-nameDownload ${
                  variant === 'sm' ? 'text-sm' : 'text-xl'
                }`}
              />
            </div>

            {previewUrl ? (
              <img
                className="object-cover w-full h-full"
                src={previewUrl}
                alt="uploaded_document"
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-green-50">
                <i className="text-2xl text-green-500 icon-nameCheck" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <label
            htmlFor={inputId}
            className={`flex items-center justify-center text-md ${
              variant === 'sm' ? 'size-16' : 'size-24'
            } rounded-lg border-[2px] border-dashed text-gray-700 flex-col cursor-pointer hover:border-blue-500 hover:text-blue-500 transition-colors`}
          >
            <i
              className={`text-gray-400 icon-namePlus-4 ${
                variant === 'sm' ? 'text-2xl' : 'text-4xl'
              }`}
            />
          </label>
          <input
            className="hidden"
            type="file"
            id={inputId}
            name={inputId}
            accept="image/png, image/jpeg, image/jpg"
            onChange={onUpload}
          />
        </div>
      )}
    </section>
  );
};

export default ImagePreviewBox;
